import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Box,
  Grid,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import MoreIcon from '@mui/icons-material/MoreVert';
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded';
import { getReservationDetails, markTripAsNoShow } from 'redux/actions/reservationAction';
import NoShowModal from 'components/noShowModal/NoShowModal';
import DispatchModal from 'pages/operation/allot/DispatchModal';
import AllotVehicleModal from 'pages/operation/allRides/AllotVehicleModal';
import CommonTableDialog from 'components/commonTableDialog/CommonTableDialog';
import CommonButton from 'components/commonButton/CommonButton';
import Loader from 'components/loader/Loader';
import {
  formatCurrency,
  getDateFormat,
  getReservationDetailsObject,
  handlePermissions,
  isValidTripTime,
  checkIsAdmin,
} from 'utils/utils';
import { roleAndPermissionEnum } from 'utils/rolesPermissionsEnum';
import { billingTypeEnum } from 'utils/enumObj';
import useModal from 'hooks/useModal';
import {
  allotDialogMenu,
  bookingDetailsData,
  bookingTime,
  allotMoreMenu,
  ongoingMoreMenu,
} from './BookingDetailsData';
import Wifi from 'assets/images/Wifi.svg';
import Snack from 'assets/images/Snack.svg';
import Charger from 'assets/images/Charger.svg';
import NewsPaper from 'assets/images/NewsPaper.svg';
import WaterBottle from 'assets/images/WaterBottle.svg';
import AssignmentActionDetails from './AssignDriverHistory';
import EditChargesDialog from './EditChargesDialog';

// Read-only input box to mimic disabled MUI text field
const ReadOnlyInput = ({ value }) => (
  <Box
    sx={{
      border: '1px solid #c4c4c4',
      borderRadius: '4px',
      padding: '8px 12px',
      minHeight: '40px',
      display: 'flex',
      alignItems: 'center',
      backgroundColor: '#f9f9f9',
      fontSize: '14px',
      color: '#333',
      width: '100%',
      boxSizing: 'border-box',
    }}
  >
    {value ?? 'NA'}
  </Box>
);

// Read-only dropdown box to mimic disabled MUI select field
const ReadOnlyDropdown = ({ label, value }) => (
  <Box>
    <Typography variant="body3" sx={{ fontSize: '12px', color: '#757575', mb: '4px' }}>
      {label}
    </Typography>
    <Box
      sx={{
        border: '1px solid #c4c4c4',
        borderRadius: '4px',
        padding: '8px 12px',
        minHeight: '40px',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#f9f9f9',
        fontSize: '14px',
        color: '#333',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {value || 'NA'}
    </Box>
  </Box>
);

// Read-only switch to mimic disabled MUI switch
const ReadOnlySwitch = ({ label, checked }) => (
  <Box>
    <Typography variant="body3" sx={{ fontSize: '12px', color: '#757575', mb: '4px' }}>
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '40px' }}>
      <Box
        sx={{
          width: '40px',
          height: '22px',
          borderRadius: '11px',
          backgroundColor: checked ? '#1976d2' : '#bdbdbd',
          position: 'relative',
          transition: 'background-color 0.2s',
        }}
      >
        <Box
          sx={{
            width: '18px',
            height: '18px',
            borderRadius: '50%',
            backgroundColor: '#fff',
            position: 'absolute',
            top: '2px',
            left: checked ? '20px' : '2px',
            transition: 'left 0.2s',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          }}
        />
      </Box>
      <Typography variant="body2" sx={{ ml: 1, fontSize: '14px', color: '#333' }}>
        {checked ? 'Yes' : 'No'}
      </Typography>
    </Box>
  </Box>
);

const BookingDetails = () => {
  const { open: allotModal, toggle: setAllotModal } = useModal();
  const { open: dispatchModal, toggle: setDipatchmodal } = useModal();
  const { open: editChargesModal, toggle: setEditChargesModal } = useModal();
  const { open: noShowModal, toggle: setNoShowModal } = useModal();

  const [open, setOpen] = useState(false);
  const [dialogMenu, setDialogMenu] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { state } = location;
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');
  const type = searchParams.get('type');
  const page = searchParams.get('page');
  const rowPerPage = searchParams.get('rowPerPage');
  const search = searchParams.get('search');
  const tab = searchParams.get('tab');

  const tripDetails = useSelector((state) => state.getReservationDetails?.reservationDetails?.data);
  const reservationDataLoading = useSelector((state) => state.getReservationDetails?.loading);
  const CabFacilities = tripDetails?.facilities;
  const tripStatus = tripDetails?.status?.toLowerCase();

  const details = getReservationDetailsObject(tripDetails);
  const role = localStorage.getItem('role');
  const pageType = location?.pathname?.split('/')[1];
  const isAdmin = checkIsAdmin();

  useEffect(() => {
    if (id) {
      dispatch(getReservationDetails(id));
    }
    btnText(tripDetails?.status);
  }, []);

  const roleAndPermissions = useSelector(
    (state) => state?.getRolesAndPermissions?.rolesAndPermissions,
  );
  const reservationRoleEnum = roleAndPermissionEnum.reservation;
  const operationRoleEnum = roleAndPermissionEnum.operation;

  const accessForall = handlePermissions(roleAndPermissions, reservationRoleEnum.moduleKey, {
    defaultAllAccess: reservationRoleEnum.all,
  });
  const accessForOnlyView = handlePermissions(roleAndPermissions, reservationRoleEnum.moduleKey, {
    customAccess: reservationRoleEnum.view,
  });
  const accessForEdit = handlePermissions(roleAndPermissions, reservationRoleEnum.moduleKey, {
    customAccess: reservationRoleEnum.edit,
  });
  const accessForAdd = handlePermissions(roleAndPermissions, reservationRoleEnum.moduleKey, {
    customAccess: reservationRoleEnum.add,
  });
  const accessForCancel = handlePermissions(roleAndPermissions, reservationRoleEnum.moduleKey, {
    customAccess: reservationRoleEnum.cancel,
  });
  const cancelAccess = accessForall || accessForCancel;

  const accessForAllot = handlePermissions(roleAndPermissions, operationRoleEnum.moduleKey, {
    customAccess: operationRoleEnum.allRides.allRidesAllot,
  });
  const accessForReallot = handlePermissions(roleAndPermissions, operationRoleEnum.moduleKey, {
    customAccess: operationRoleEnum.allotTrip.allotTripReallot,
  });
  const accessForDispatch = handlePermissions(roleAndPermissions, operationRoleEnum.moduleKey, {
    customAccess: operationRoleEnum.allotTrip.allotTripDispatch,
  });
  const accessForDispatchCancel = handlePermissions(
    roleAndPermissions,
    operationRoleEnum.moduleKey,
    { customAccess: operationRoleEnum.dispatch.dispatchCancel },
  );
  const accessForGenerate = handlePermissions(roleAndPermissions, operationRoleEnum.moduleKey, {
    customAccess: operationRoleEnum.billing.billingGenerate,
  });
  const accessForNoShow = handlePermissions(roleAndPermissions, operationRoleEnum.moduleKey, {
    customAccess: operationRoleEnum.dispatch.dispatchCancel,
  });

  const editAccess = accessForAllot || accessForDispatch || accessForGenerate;
  const reservationEdit = accessForall || accessForEdit;

  useEffect(() => {
    const isAssignedToSubVendor =
      (tripDetails?.subVendorCompanyId ||
        tripDetails?.additionalInfo?.subVendorAssignment?.subVendorCompanyId ||
        tripDetails?.subVendorCompany ||
        tripDetails?.additionalInfo?.subVendorAssignmentStatus === 'alloted') &&
      tripDetails?.additionalInfo?.subVendorAssignmentStatus !== 'rejected';

    if (tripStatus === 'booked' || tripStatus === 'pending') {
      setDialogMenu(allotDialogMenu);
    } else if (
      (tripStatus === 'dispatched' || tripStatus === 'alloted') &&
      (accessForDispatch || accessForDispatchCancel || (isAdmin && accessForReallot))
    ) {
      if (isAssignedToSubVendor || (isAdmin && !accessForReallot)) {
        setDialogMenu(allotMoreMenu.filter((m) => m.label !== 'Re-Allot'));
      } else {
        setDialogMenu(allotMoreMenu);
      }
    } else if (
      tripStatus === 'ongoing' &&
      (accessForDispatch || accessForDispatchCancel || (isAdmin && accessForReallot))
    ) {
      if (isAssignedToSubVendor || (isAdmin && !accessForReallot)) {
        setDialogMenu(ongoingMoreMenu.filter((m) => m.label !== 'Re-Allot'));
      } else {
        setDialogMenu(ongoingMoreMenu);
      }
    } else if (tripStatus === 'alloted' && role === 'adminCompany') {
      setDialogMenu(allotDialogMenu);
    } else {
      setDialogMenu('');
    }
  }, [tripDetails, accessForDispatch, accessForDispatchCancel, accessForReallot, role]);

  const btnText = (status) => {
    if (pageType === 'reservation') {
      switch (status) {
        case 'booked':
        case 'pending':
          return 'Edit';
        case 'alloted':
          return 'Edit';
        default:
          return '';
      }
    } else if (pageType === 'operation') {
      if (status === 'booked' && accessForAllot) {
        const isAssignedToSubvendor =
          (tripDetails?.subVendorCompanyId ||
            tripDetails?.additionalInfo?.subVendorAssignment?.subVendorCompanyId ||
            tripDetails?.subVendorCompany ||
            tripDetails?.additionalInfo?.subVendorAssignmentStatus === 'alloted') &&
          tripDetails?.additionalInfo?.subVendorAssignmentStatus !== 'rejected';
        if (isAssignedToSubvendor) return '';
        return 'Allot Duty';
      } else if (status === 'alloted' && accessForDispatch) {
        return 'Dispatch Duty';
      } else if (status === 'cancelled') {
        return 'Generate Invoice';
      } else {
        return '';
      }
    }
  };

  let btnName = btnText(tripStatus);

  const handleOnError = (e, facilityName) => {
    let replacementImage;
    switch (facilityName) {
      case 'Wifi':
        replacementImage = Wifi;
        break;
      case 'Snack':
        replacementImage = Snack;
        break;
      case 'Charger':
        replacementImage = Charger;
        break;
      case 'News Paper':
        replacementImage = NewsPaper;
        break;
      case 'Water Bottel':
        replacementImage = WaterBottle;
        break;
      default:
        replacementImage = Wifi;
    }
    e.target.src = replacementImage;
  };

  const handleDisabledBtn = (btnName) => {
    if (
      (btnName?.toLowerCase() === 'edit' && !isValidTripTime(tripDetails?.scheduledTime)) ||
      tripDetails?.Invoices?.length > 0
    ) {
      return true;
    }
  };

  const actionBtn = (status, tripDetails) => {
    if (pageType === 'reservation') {
      if (status === 'booked' || status === 'alloted' || status === 'pending') {
        const dateStatus = isValidTripTime(tripDetails?.scheduledTime);
        if (dateStatus) {
          navigate('/reservation/new-reservation', {
            state: { edit: true, reservationProfile: tripDetails },
          });
        } else {
          toast.error('You are not allowed to edit the trip at this time');
        }
      }
    } else if (pageType === 'operation') {
      if (status === 'booked') {
        setAllotModal();
      } else if (status === 'alloted') {
        setDipatchmodal();
      } else if (status === 'cancelled') {
        navigate(
          `/operation/billing/performa-invoice?tripId=${tripDetails?.id}&companyId=${tripDetails?.companyId}&companyName=${tripDetails?.companyName}&cityId=${tripDetails?.city}&cityName=${tripDetails?.location}&status=${tab}&page=${page}&pageRowSize=${rowPerPage}&startDate=${startDate}&endDate=${endDate}&billingType=${parseInt(details?.billingType) === billingTypeEnum?.billToCompany ? 'billToCompany' : 'billToCreditCard'}&pageType=generateInvoice`,
        );
      }
    }
  };

  const handleDialog = (event) => {
    setOpen(event.currentTarget);
  };

  const handleNoShowSubmit = (noShowData) => {
    dispatch(markTripAsNoShow(noShowData, 'details'));
    setNoShowModal();
  };

  const handleMoreIcon = () => {
    const alwaysHiddenStatuses = ['cancelled', 'completed', 'closed', 'noshow'];
    if (tripDetails?.Invoices?.length > 0) return false;
    if (
      tripStatus === 'dispatched' &&
      !accessForDispatch &&
      !accessForDispatchCancel &&
      !(isAdmin && accessForReallot)
    )
      return false;
    if (
      tripStatus === 'ongoing' &&
      !accessForDispatch &&
      !accessForDispatchCancel &&
      !(isAdmin && accessForReallot)
    )
      return false;
    return !alwaysHiddenStatuses.includes(tripStatus);
  };

  const filterValue = (key) => {
    const keys = [
      'Reporting Address',
      'Drop Address',
      'Via Address',
      'Landmark',
      'Special Instruction',
      'Trip Charges',
      'Flight No',
      'Cancellation Reason',
      'No-Show Reason',
      'Driver Remarks',
    ];
    if (key === 'Special Instruction' && tripDetails?.DriverProfile) return '';
    else if (keys.includes(key)) return 'expandRow';
  };

  const handleBackBtn = () => {
    const allRidesTab = ['all', 'booked', 'closed', 'cancelled', 'completed'];
    if (pageType === 'operation') {
      if (type === 'allrides') {
        const navigateStatus =
          tab === 'all' ? 'all' : allRidesTab?.includes(tab) ? tripStatus : null;
        navigate(
          `/operation/${type}/${navigateStatus}?startDate=${startDate || null}&endDate=${endDate || null}&page=${page}&rowsPerPage=${rowPerPage}&search=${search || ''}`,
        );
      } else if (type === 'allot' || type === 'dispatch') {
        navigate(
          `/operation/${type}?startDate=${startDate || null}&endDate=${endDate || null}&page=${page}&rowsPerPage=${rowPerPage}&search=${search || ''}`,
        );
      }
    } else {
      navigate(-1);
    }
  };

  // Parse rate card data to match the exact UI structure based on duty type
  const rateCardData = useMemo(() => {
    const rc = tripDetails?.rateConfig;
    if (!rc) return null;

    const isPackageType = rc.rateType === '/package' || rc.rateType === 'package';
    const dutyType = tripDetails?.dutyTypeName;
    const isRental = dutyType === 'Rental';
    const isOutstation = dutyType === 'Outstation';

    // Prepare package rows for the table (handle both array and single object)
    const nested = rc.rateConfig;
    let packages = [];
    if (isPackageType && nested) {
      packages = Array.isArray(nested) ? nested : [nested];
    }

    return {
      isPackageType,
      isRental,
      isOutstation,
      // Fields for Airport/Ride grid layout
      baseRate: rc.baseRate,
      surCharge: rc.surCharge,
      platformCharge: rc.platformCharge,
      perKmRate: nested?.extraKM?.rate || rc.currentRate,
      nightAllowance: nested?.nightAllowance?.rate,
      earlyStartAllowance: nested?.earlyStartAllowance?.rate,
      // Rows for Rental/Outstation table layout
      packages,
    };
  }, [tripDetails?.rateConfig, tripDetails?.dutyTypeName]);

  // Helper function to capitalize the first letter of duration type
  const capitalize = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : '');

  // Common table header cell styling to match the screenshot color
  const headerCellStyle = {
    fontWeight: 600,
    fontSize: '14px',
    color: '#ffffff',
    backgroundColor: '#0F538D',
    whiteSpace: 'nowrap',
    padding: '10px 12px',
  };

  // Common table body cell styling
  const bodyCellStyle = {
    fontSize: '14px',
    fontWeight: 500,
    color: '#333',
    whiteSpace: 'nowrap',
    padding: '8px 12px',
  };

  return (
    <>
      {reservationDataLoading ? (
        <Loader />
      ) : (
        <Box>
          <Grid container className="pb20 justifyBetween">
            <Box className="flex" alignItems={'center'}>
              <ArrowBackIosRoundedIcon
                className="font24 colorGrey"
                role="presentation"
                onClick={() => handleBackBtn()}
              />
              <Typography variant={'head4'} className="colorPrimary pl10">
                Booking ID {tripDetails?.tripId}
              </Typography>
            </Box>
            <Box className="flex" gap={2}>
              {btnName && (pageType === 'reservation' ? reservationEdit : editAccess) && (
                <CommonButton
                  name={btnName}
                  iconType={`${btnName.toLowerCase()}`}
                  iconPlace="front"
                  btnClass={`btnSm`}
                  disabled={handleDisabledBtn(btnName)}
                  onClick={() => actionBtn(tripStatus, tripDetails)}
                />
              )}
              {pageType === 'operation' &&
                (tripStatus === 'completed' ||
                  tripStatus === 'dispatched' ||
                  tripStatus === 'ongoing') &&
                editAccess && (
                  <CommonButton
                    name="Edit Duty Slip"
                    iconType="edit"
                    iconPlace="front"
                    btnClass="btnSm"
                    disabled={tripDetails?.Invoices?.length > 0}
                    onClick={() => setEditChargesModal()}
                  />
                )}
              {pageType === 'operation' &&
                tripStatus === 'dispatched' &&
                (accessForNoShow || isAdmin) && (
                  <CommonButton
                    name="Mark No-Show"
                    btnClass="btnSm"
                    disabled={tripDetails?.Invoices?.length > 0}
                    onClick={() => setNoShowModal()}
                  />
                )}
            </Box>
          </Grid>
          <Box className="dashedBorderBottom"></Box>
          <Grid container justifyContent={'center'}>
            <Grid item xs={11}>
              <Grid container className="ptb30 justifyBetween">
                <Typography variant="head5" className="fw600 colorBlack">
                  Booking Details&nbsp;
                  <span className="font14 fw400">
                    ({getDateFormat(tripDetails?.updatedAt, 'fromNowFormat')})
                  </span>
                </Typography>
                {tripStatus === 'completed' && (
                  <Typography variant={'head5'} className="colorPrimary fw600">
                    Total Trip Charges - ₹&nbsp;
                    {formatCurrency(tripDetails?.finalPrice)}
                  </Typography>
                )}
                {handleMoreIcon() && (cancelAccess || (isAdmin && accessForReallot)) && (
                  <IconButton
                    aria-label="open drawer"
                    edge="start"
                    onClick={(event) => handleDialog(event)}
                    className="pl25"
                  >
                    <MoreIcon />
                  </IconButton>
                )}
              </Grid>

              <Grid container className="ptb20 mb20 bgLightOrange">
                {bookingTime &&
                  bookingTime(tripDetails).map((data, index) => (
                    <Grid
                      key={index}
                      item
                      xs={12}
                      sm={3}
                      sx={{
                        padding: { xs: '20px 0px 0px 20px', sm: '0px' },
                        justifyContent: { xs: 'flex-start', sm: 'center' },
                      }}
                      container
                      className="justifyCenter"
                    >
                      <Box className="flex">
                        <img src={data.img} alt="Logo" />
                      </Box>
                      <Typography variant={'body2'} className="colorBlack pl20">
                        {data.title || 'NA'}
                      </Typography>
                    </Grid>
                  ))}
              </Grid>

              {/* Booking Details Grid */}
              <Grid container className="mt40 mb20">
                {bookingDetailsData(tripDetails, details) &&
                  bookingDetailsData(tripDetails, details).map((value, key) => (
                    <Grid
                      key={key}
                      item
                      xs={12}
                      md={filterValue(value?.title, value?.status) ? 12 : 6}
                      sx={{ display: value?.title ? 'flex' : 'none' }}
                      container
                    >
                      <Grid
                        xs={4}
                        md={filterValue(value?.title, value?.status) ? 2 : 4}
                        item
                        className="p20 alignStart gridBorder"
                      >
                        <Typography variant={'body2'} className="colorGrey">
                          {value.title || 'NA'}
                        </Typography>
                      </Grid>
                      <Grid
                        xs={8}
                        md={filterValue(value?.title, value?.status) ? 10 : 8}
                        item
                        flexWrap={'wrap'}
                        className="p20 alignStart gridBorder"
                      >
                        <Typography
                          variant={`${value?.textType === 'textBold' ? 'head5' : 'body2'}`}
                          className={`pr10 ${value?.textType === 'textBold' && 'fw700'}`}
                        >
                          {value.data || 'NA'}
                          {value.title === 'Duty Type' &&
                            tripDetails?.additionalInfo?.isMultiDay && (
                              <span className="font12 fontNormal colorGrey">{' (Multi-day)'}</span>
                            )}
                        </Typography>
                        {value?.inclusive && (
                          <Typography variant={'body3'} className={'colorGrey'}>
                            (Exclusive of GST and Toll & Parking)
                          </Typography>
                        )}
                        {value.title === 'Passenger Name' && value.isVip && (
                          <Typography variant={'body5'} className="vipBadge">
                            VIP
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  ))}
              </Grid>

              {/* Rate Card Section matching exact Company Profile UI structure */}
              {rateCardData && (
                <Box className="mt40 mb20">
                  <Typography variant="head5" component={'h2'} className="colorBlack fw600 mb20">
                    Rate Card
                  </Typography>

                  {/* Top filter bar mimicking dropdown selections */}
                  <Grid container spacing={2} className="mt20">
                    {/* <Grid item md={3} sm={6} xs={12}>
                      <ReadOnlyDropdown
                        label="City"
                        value={tripDetails?.ServiceCity?.name}
                      />
                    </Grid>
                    <Grid item md={3} sm={6} xs={12}>
                      <ReadOnlyDropdown
                        label="Service Type"
                        value={tripDetails?.dutyTypeName}
                      />
                    </Grid>
                    <Grid item md={3} sm={6} xs={12}>
                      <ReadOnlyDropdown
                        label="Car Type"
                        value={tripDetails?.carType}
                      />
                    </Grid> */}
                    {/* Show Multi Day toggle only for Rental */}
                    {rateCardData.isRental && (
                      <Grid item md={3} sm={6} xs={12}>
                        <ReadOnlySwitch
                          label="Multi Day"
                          checked={!!tripDetails?.additionalInfo?.isMultiDay}
                        />
                      </Grid>
                    )}
                  </Grid>

                  {rateCardData.isRental ? (
                    /* Table layout for Rental and Outstation with all 12 columns and horizontal scroll */
                    <Box
                      sx={{
                        mt: 3,
                        overflowX: 'auto',
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px',
                      }}
                    >
                      <Table size="small" sx={{ minWidth: '1400px' }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={headerCellStyle}>Pack</TableCell>
                            <TableCell sx={headerCellStyle}>Duration</TableCell>
                            <TableCell sx={headerCellStyle}>Km</TableCell>
                            <TableCell sx={headerCellStyle}>Price</TableCell>
                            <TableCell sx={headerCellStyle}>Grace Km</TableCell>
                            <TableCell sx={headerCellStyle}>Extra/Km</TableCell>
                            <TableCell sx={headerCellStyle}>Grace Hr</TableCell>
                            <TableCell sx={headerCellStyle}>Extra/Hr</TableCell>
                            <TableCell sx={headerCellStyle}>Night Allowance</TableCell>
                            <TableCell sx={headerCellStyle}>Early Start Allowance</TableCell>

                            <TableCell sx={headerCellStyle}>Adv Charge</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rateCardData.packages.map((pkg, idx) => {
                            // Format the package name (e.g., "8 Hour", "1 Day")
                            const packName =
                              pkg.name || `${pkg.duration || 0} ${capitalize(pkg.durationType)}`;
                            // Format duration value
                            const durationVal = `${pkg.duration || 0} ${capitalize(pkg.durationType)}`;
                            // Format km value
                            const kmVal = `${pkg.maxDistanceKms || 0}km`;
                            // Format additional charges into a single string
                            let advChargeStr = 'NA';
                            if (
                              Array.isArray(pkg.additionalCharges) &&
                              pkg.additionalCharges.length > 0
                            ) {
                              advChargeStr = pkg.additionalCharges
                                .map((c) => `${c.name}: ${c.rate}`)
                                .join(', ');
                            }

                            return (
                              <TableRow key={idx} hover>
                                <TableCell sx={bodyCellStyle}>{packName}</TableCell>
                                <TableCell sx={bodyCellStyle}>{durationVal}</TableCell>
                                <TableCell sx={bodyCellStyle}>{kmVal}</TableCell>
                                <TableCell sx={bodyCellStyle}>{pkg.cost || 0}</TableCell>
                                <TableCell sx={bodyCellStyle}>
                                  {pkg.extraKM?.graceValue || 0}
                                </TableCell>
                                <TableCell sx={bodyCellStyle}>{pkg.extraKM?.rate || 0}</TableCell>
                                <TableCell
                                  sx={bodyCellStyle}
                                >{`${pkg.extraHrs?.graceValue || 0} Hr`}</TableCell>
                                <TableCell sx={bodyCellStyle}>{pkg.extraHrs?.rate || 0}</TableCell>
                                <TableCell sx={bodyCellStyle}>
                                  {pkg.nightAllowance?.rate || 0}
                                </TableCell>
                                <TableCell sx={bodyCellStyle}>
                                  {pkg.earlyStartAllowance?.rate || 0}
                                </TableCell>

                                <TableCell sx={bodyCellStyle}>{advChargeStr}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Box>
                  ) : rateCardData.isOutstation ? (
                    /* Form layout for Outstation — matches ReservationRateCardForm fields */
                    <Box sx={{ mt: 3 }}>
                      <Box
                        sx={{
                          backgroundColor: '#f5f5f5',
                          p: 1.5,
                          borderRadius: '4px 4px 0 0',
                          border: '1px solid #e0e0e0',
                          borderBottom: 'none',
                        }}
                      >
                        <Typography variant="body2" className="fw600 colorBlack">
                          Basic Configuration
                        </Typography>
                      </Box>
                      <Grid
                        container
                        sx={{
                          border: '1px solid #e0e0e0',
                          borderRadius: '0 0 4px 4px',
                          p: 2,
                          gap: 2,
                        }}
                      >
                        <Grid item md={4} sm={6} xs={12}>
                          <Typography
                            variant="body3"
                            className="colorGrey mb5"
                            sx={{ fontSize: '12px' }}
                          >
                            Base Fare
                          </Typography>
                          <ReadOnlyInput value={rateCardData.baseRate} />
                        </Grid>
                        <Grid item md={4} sm={6} xs={12}>
                          <Typography
                            variant="body3"
                            className="colorGrey mb5"
                            sx={{ fontSize: '12px' }}
                          >
                            Surcharge
                          </Typography>
                          <ReadOnlyInput value={rateCardData.surCharge} />
                        </Grid>
                        <Grid item md={4} sm={6} xs={12}>
                          <Typography
                            variant="body3"
                            className="colorGrey mb5"
                            sx={{ fontSize: '12px' }}
                          >
                            Platform Charge
                          </Typography>
                          <ReadOnlyInput value={rateCardData.platformCharge} />
                        </Grid>
                      </Grid>

                      <Box
                        sx={{
                          backgroundColor: '#f5f5f5',
                          p: 1.5,
                          mt: 2,
                          borderRadius: '4px 4px 0 0',
                          border: '1px solid #e0e0e0',
                          borderBottom: 'none',
                        }}
                      >
                        <Typography variant="body2" className="fw600 colorBlack">
                          Outstation Package Configuration
                        </Typography>
                      </Box>
                      <Grid
                        container
                        sx={{
                          border: '1px solid #e0e0e0',
                          borderRadius: '0 0 4px 4px',
                          p: 2,
                          gap: 2,
                        }}
                      >
                        {(() => {
                          const pkg = rateCardData.packages?.[0] || {};
                          const advCharge = pkg.additionalCharges?.[0]?.rate;
                          return (
                            <>
                              <Grid item md={3} sm={6} xs={12}>
                                <Typography
                                  variant="body3"
                                  className="colorGrey mb5"
                                  sx={{ fontSize: '12px' }}
                                >
                                  Days
                                </Typography>
                                <ReadOnlyInput value={pkg.duration} />
                              </Grid>
                              <Grid item md={3} sm={6} xs={12}>
                                <Typography
                                  variant="body3"
                                  className="colorGrey mb5"
                                  sx={{ fontSize: '12px' }}
                                >
                                  Max Distance (Km)
                                </Typography>
                                <ReadOnlyInput value={pkg.maxDistanceKms} />
                              </Grid>
                              <Grid item md={3} sm={6} xs={12}>
                                <Typography
                                  variant="body3"
                                  className="colorGrey mb5"
                                  sx={{ fontSize: '12px' }}
                                >
                                  Price
                                </Typography>
                                <ReadOnlyInput value={pkg.cost} />
                              </Grid>
                              <Grid item md={3} sm={6} xs={12}>
                                <Typography
                                  variant="body3"
                                  className="colorGrey mb5"
                                  sx={{ fontSize: '12px' }}
                                >
                                  Grace Hr
                                </Typography>
                                <ReadOnlyInput value={pkg.extraHrs?.graceValue} />
                              </Grid>
                              <Grid item md={3} sm={6} xs={12}>
                                <Typography
                                  variant="body3"
                                  className="colorGrey mb5"
                                  sx={{ fontSize: '12px' }}
                                >
                                  Extra Price/Hr
                                </Typography>
                                <ReadOnlyInput value={pkg.extraHrs?.rate} />
                              </Grid>
                              <Grid item md={3} sm={6} xs={12}>
                                <Typography
                                  variant="body3"
                                  className="colorGrey mb5"
                                  sx={{ fontSize: '12px' }}
                                >
                                  Grace Km
                                </Typography>
                                <ReadOnlyInput value={pkg.extraKM?.graceValue} />
                              </Grid>
                              <Grid item md={3} sm={6} xs={12}>
                                <Typography
                                  variant="body3"
                                  className="colorGrey mb5"
                                  sx={{ fontSize: '12px' }}
                                >
                                  Extra Price/Km
                                </Typography>
                                <ReadOnlyInput value={pkg.extraKM?.rate} />
                              </Grid>
                              <Grid item md={3} sm={6} xs={12}>
                                <Typography
                                  variant="body3"
                                  className="colorGrey mb5"
                                  sx={{ fontSize: '12px' }}
                                >
                                  Advance Booking Charge
                                </Typography>
                                <ReadOnlyInput value={advCharge} />
                              </Grid>
                              <Grid item md={3} sm={6} xs={12}>
                                <Typography
                                  variant="body3"
                                  className="colorGrey mb5"
                                  sx={{ fontSize: '12px' }}
                                >
                                  Night Allowance (per night)
                                </Typography>
                                <ReadOnlyInput value={pkg.nightAllowance?.rate} />
                              </Grid>
                              <Grid item md={3} sm={6} xs={12}>
                                <Typography
                                  variant="body3"
                                  className="colorGrey mb5"
                                  sx={{ fontSize: '12px' }}
                                >
                                  Early Start Allowance
                                </Typography>
                                <ReadOnlyInput value={pkg.earlyStartAllowance?.rate} />
                              </Grid>
                              <Grid item md={3} sm={6} xs={12}>
                                <Typography
                                  variant="body3"
                                  className="colorGrey mb5"
                                  sx={{ fontSize: '12px' }}
                                >
                                  Driver Allowance (per day)
                                </Typography>
                                <ReadOnlyInput value={pkg.driverAllowance?.rate} />
                              </Grid>
                            </>
                          );
                        })()}
                      </Grid>
                    </Box>
                  ) : (
                    /* Grid layout for Airport Transfer and Ride */
                    <>
                      <Box sx={{ mt: 3 }}>
                        <Box
                          sx={{
                            backgroundColor: '#f5f5f5',
                            p: 1.5,
                            borderRadius: '4px 4px 0 0',
                            border: '1px solid #e0e0e0',
                            borderBottom: 'none',
                          }}
                        >
                          <Typography variant="body2" className="fw600 colorBlack">
                            Basic Configuration
                          </Typography>
                        </Box>
                        <Grid
                          container
                          sx={{
                            border: '1px solid #e0e0e0',
                            borderRadius: '0 0 4px 4px',
                            p: 2,
                            gap: 2,
                          }}
                        >
                          <Grid item md={4} sm={6} xs={12}>
                            <Typography
                              variant="body3"
                              className="colorGrey mb5"
                              sx={{ fontSize: '12px' }}
                            >
                              Base Rate
                            </Typography>
                            <ReadOnlyInput value={rateCardData.baseRate} />
                          </Grid>
                          <Grid item md={4} sm={6} xs={12}>
                            <Typography
                              variant="body3"
                              className="colorGrey mb5"
                              sx={{ fontSize: '12px' }}
                            >
                              Surcharge
                            </Typography>
                            <ReadOnlyInput value={rateCardData.surCharge} />
                          </Grid>
                          <Grid item md={4} sm={6} xs={12}>
                            <Typography
                              variant="body3"
                              className="colorGrey mb5"
                              sx={{ fontSize: '12px' }}
                            >
                              Platform Charge
                            </Typography>
                            <ReadOnlyInput value={rateCardData.platformCharge} />
                          </Grid>
                          <Grid item md={4} sm={6} xs={12}>
                            <Typography
                              variant="body3"
                              className="colorGrey mb5"
                              sx={{ fontSize: '12px' }}
                            >
                              /KM
                            </Typography>
                            <ReadOnlyInput value={rateCardData.perKmRate} />
                          </Grid>
                          <Grid item md={4} sm={6} xs={12}>
                            <Typography
                              variant="body3"
                              className="colorGrey mb5"
                              sx={{ fontSize: '12px' }}
                            >
                              Night Allowance
                            </Typography>
                            <ReadOnlyInput value={rateCardData.nightAllowance} />
                          </Grid>
                          <Grid item md={4} sm={6} xs={12}>
                            <Typography
                              variant="body3"
                              className="colorGrey mb5"
                              sx={{ fontSize: '12px' }}
                            >
                              Early Start Allowance
                            </Typography>
                            <ReadOnlyInput value={rateCardData.earlyStartAllowance} />
                          </Grid>
                        </Grid>
                      </Box>

                      {/* Per KM Rate Configuration Section */}
                      {/* {rateCardData.perKmRate && (
                        <Box sx={{ mt: 3 }}>
                          <Box
                            sx={{
                              backgroundColor: "#f5f5f5",
                              p: 1.5,
                              borderRadius: "4px 4px 0 0",
                              border: "1px solid #e0e0e0",
                              borderBottom: "none",
                            }}
                          >
                            <Typography
                              variant="body2"
                              className="fw600 colorBlack"
                            >
                              /km Rate Configuration
                            </Typography>
                          </Box>
                          <Grid
                            container
                            sx={{
                              border: "1px solid #e0e0e0",
                              borderRadius: "0 0 4px 4px",
                              p: 2,
                            }}
                          >
                            <Grid item md={4} sm={6} xs={12}>
                              <Typography
                                variant="body3"
                                className="colorGrey mb5"
                                sx={{ fontSize: "12px" }}
                              >
                                /km Charge
                              </Typography>
                              <ReadOnlyInput value={rateCardData.perKmRate} />
                            </Grid>
                          </Grid>
                        </Box>
                      )} */}
                    </>
                  )}
                </Box>
              )}

              {tripStatus === 'completed' ? (
                <img
                  src={tripDetails?.tripRouteImage?.url}
                  alt="map"
                  className="width100 height400"
                />
              ) : (
                <>
                  <Typography variant="head5" component={'h2'} className="colorBlack fw600 mb20">
                    Facilities
                  </Typography>
                  <Grid container className="mt30 mb20">
                    {CabFacilities?.length ? (
                      CabFacilities?.map((row, index) => (
                        <Grid
                          item
                          md={2.4}
                          sm={6}
                          xs={12}
                          key={index}
                          className="facilityDashBoarder"
                        >
                          <Box className="justifyCenter ptb20">
                            <Box className="flex" alignItems={'center'}>
                              <img
                                src={row?.img?.url}
                                alt="Logo"
                                className="width40 height40 objectFit"
                                onError={(e) => handleOnError(e, row?.name)}
                              />
                            </Box>
                            <Typography variant="body2" className="colorGrey pl20">
                              {row?.name || 'NA'}
                            </Typography>
                          </Box>
                        </Grid>
                      ))
                    ) : (
                      <Typography variant="body2" className="colorGrey pl20">
                        No Facilites Available
                      </Typography>
                    )}
                  </Grid>
                </>
              )}
            </Grid>
            {(tripStatus === 'booked' ||
              tripStatus === 'alloted' ||
              tripStatus === 'dispatched' ||
              tripStatus === 'pending') && (
              <Grid className="colorGrey font18 mb30 justifyCenter" container>
                <Grid item xs={11}>
                  <Typography variant="head5" className="fw600 colorBlack">
                    Cancellation Note
                  </Typography>
                  <li>
                    There will be no cancellation charge if the trip is cancelled before 2 hours of
                    the scheduled time.
                  </li>
                  <li>
                    There will be minimum cancellation charge if the trip is cancelled after 2 hours
                    of the scheduled time.
                  </li>
                </Grid>
              </Grid>
            )}
            <AssignmentActionDetails
              assignmentActions={tripDetails?.additionalInfo?.assignmentAction}
              currentVehicleType={
                tripDetails?.Vehicle?.carType || tripDetails?.DriverProfile?.Vehicle?.carType
              }
            />
          </Grid>
        </Box>
      )}

      <AllotVehicleModal
        open={allotModal}
        toggle={setAllotModal}
        tripDetails={tripDetails}
        page={'details'}
      />
      <DispatchModal
        open={dispatchModal}
        toggle={setDipatchmodal}
        tripDetails={tripDetails}
        page={'details'}
      />
      <CommonTableDialog menuData={dialogMenu} open={open} setOpen={setOpen} data={tripDetails} />
      <EditChargesDialog
        open={editChargesModal}
        handleClose={setEditChargesModal}
        tripId={tripDetails?.id}
        tripDetails={tripDetails}
        onSave={() => dispatch(getReservationDetails(id))}
      />
      {noShowModal && (
        <NoShowModal
          open={noShowModal}
          toggle={setNoShowModal}
          tripDetails={tripDetails}
          onSubmit={handleNoShowSubmit}
        />
      )}
    </>
  );
};

export default BookingDetails;
